import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { RxActions, rxActions } from '@rx-angular/state/actions';
import { isDynamicImportError } from 'app/core/guard/global-error.interceptor';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { NavigationState } from 'app/core/navigation/navigation.state';
import { Navigation } from 'app/core/navigation/navigation.types';
import { PopupService } from 'app/core/services/popup.service';
import { catchError, filter, of, switchMap } from 'rxjs';

interface INavigationAction {
  get: void;
  getSuccess: {
    navigation: Navigation;
  };
}

@Injectable({
  providedIn: 'root'
})
export class NavigationAction {
  actions: RxActions<INavigationAction> = rxActions<INavigationAction>();

  private readonly _navigationService: NavigationService = inject(NavigationService);
  private readonly _navigationState: NavigationState = inject(NavigationState);
  private readonly _popupService: PopupService = inject(PopupService);

  constructor() {
    this._subscribeToGetNavigationSuccess$();
    this._subscribeToGetNavigationEffect$();
  }

  private _subscribeToGetNavigationSuccess$(): void {
    this.actions.getSuccess$.subscribe({
      next: ({ navigation }: { navigation: Navigation }) => {
        this._navigationState.set({
          navigation
        });
      }
    });
  }

  private _subscribeToGetNavigationEffect$(): void {
    this.actions.onGet(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._navigationService.get>;

        return payload$.pipe(
          switchMap(() =>
            this._navigationService.get().pipe<APIResponse, APIResponse>(
              catchError((error: any) => {
                if (isDynamicImportError(error)) {
                  this._popupService.messageSnackbar({
                    icon: 'heroicons_solid:exclamation-circle',
                    message: 'Page updated. Please refresh your browser to continue.',
                    type: 'info',
                    action: 'OK',
                    onPress: () => {
                      window.location.reload();
                    }
                  });
                }
                return of(error);
              }),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (navigation) => {
        this.actions.getSuccess({ navigation });
      }
    );
  }
}
